import { ContentPropertyData, CreativeTypes, type ElementTypes, TextStyle } from '@bynder-studio/render-core';
import ProxyServerConnector from '../web/src/services/connectors/ProxyServerConnector';

export interface ComponentElement {
    id: string | number;
    type: ElementTypes;
    name: string;
    properties: Record<string, any>;
    children?: Record<string, ComponentElement>;
}

export interface Component {
    id: number;
    name: string;
    description: string;
    collectionId: number;
    collectionName: string;
    type: CreativeTypes;
    created: string;
    thumbnailUrl?: string;
    elements: ComponentElement[];
    textStyles: TextStyle[];
    contentProperties: ContentPropertyData[];
}

export type PartialComponent = Omit<Component, 'elements' | 'textStyles' | 'contentProperties'>;

export interface AddComponentRequest {
    name: string;
    description: string;
    collectionId: number;
    type: CreativeTypes;
    elements: any[];
    textStyles: TextStyle[];
    contentProperties: ContentPropertyData[];
}

export type EditComponentRequest = Omit<
    PartialComponent,
    'id' | 'created' | 'type' | 'description' | 'collectionName'
> & {
    description?: string;
};

interface GetComponentsParams {
    size: number;
    page: number;
    search?: string;
    sort?: [string, string];
    types?: CreativeTypes[];
    collectionId?: number;
}

export interface GetComponentsResponse {
    content: PartialComponent[];
    pageable: {
        pageNumber: number;
        pageSize: number;
        sort: {
            empty: boolean;
            sorted: boolean;
            unsorted: boolean;
        };
        offset: number;
        paged: boolean;
        unpaged: boolean;
    };
    last: boolean;
    totalElements: number;
    totalPages: number;
    first: boolean;
    size: number;
    number: number;
    sort: {
        empty: boolean;
        sorted: boolean;
        unsorted: boolean;
    };
    numberOfElements: number;
    empty: boolean;
}

class ComponentService extends ProxyServerConnector {
    gateway: ProxyServerConnector;

    constructor(path = '/template-presets') {
        super(path);
        this.gateway = new ProxyServerConnector(path, GATEWAY_PREFIX);
    }

    addComponent(component: AddComponentRequest) {
        return this.gateway.post('', component);
    }

    editComponent(id: number, component: EditComponentRequest) {
        return this.gateway.put(`/${id}`, component);
    }

    getComponents({ size, page, search, sort, types, collectionId }: GetComponentsParams) {
        const params = new URLSearchParams({
            size: size.toString(),
            page: page.toString(),
        });

        if (search) {
            params.append('search', search);
        }

        if (sort?.length) {
            params.append('sort', sort.join(','));
        }

        if (types?.length) {
            params.append('types', types.join(','));
        }

        if (collectionId !== undefined) {
            params.append('collectionId', collectionId.toString());
        }

        return this.gateway.get<GetComponentsResponse>(`?${params.toString()}`);
    }

    getComponentById(id: string) {
        return this.gateway.get<Component>(`/${id}`);
    }

    deleteComponent(id: number) {
        return this.gateway.delete(`/${id}`);
    }

    async storeThumbnail(componentId: number, dataUrl: string): Promise<{ success: boolean }> {
        const blob = this.dataUrlToBlob(dataUrl);
        const file = new File([blob], 'thumbnail.png', { type: blob.type });

        const formData = new FormData();
        formData.append('file', file);

        const response = await this.gateway.post(`/${componentId}/thumbnail`, formData as any);

        return { success: response.status === 200 };
    }

    private dataUrlToBlob(dataUrl: string): Blob {
        const [header, base64Data] = dataUrl.split(',');
        const mimeMatch = header.match(/:(.*?);/);
        const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';
        const byteString = atob(base64Data);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const uint8Array = new Uint8Array(arrayBuffer);

        for (let i = 0; i < byteString.length; i++) {
            uint8Array[i] = byteString.charCodeAt(i);
        }

        return new Blob([uint8Array], { type: mimeType });
    }
}

export default new ComponentService();
